import { useEffect, useRef, useState } from "react";
import { HeaderRightContent, StyledHeader } from "../Common/styles/header";
import {
  Box,
  CircularProgress,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  NoDataContainer,
  StickyStyle,
  StyledTableCell,
  TableBorderRadiusTopLeftRight,
  pageSize,
} from "../Common/styles/table";
import DataTableLayout from "../Common/UI/DataTableLayout";
import DataTableFooter from "../Common/UI/DataTableFooter";
import {
  dataTableListShell,
  dataTablePageShell,
} from "../../utils/dataTableShell";
import { AxiosResponse } from "axios";
import http from "../../utils/http";
import { errorToastMessage, toastMessage } from "../../utils/toast";
import { useParams } from "react-router-dom";
import { CompletedStatusTag } from "../Common/UI/StatusTag";
import { DateTime } from "luxon";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import { RefreshOutlined } from "@mui/icons-material";
import { formatEventName } from "../../utils/audit";

const eventType: any = {
  export_study: "Export study",
  export_data_dictionary: "Export data dictionary",
};

const Export = () => {
  const [toggle, setToggle] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [exportData, setExportData] = useState<any>([]);
  const [exportDataCount, setExportDataCount] = useState<number>(0);
  const [downloadingRowId, setDownloadingRowId] = useState<string>("");

  const { id } = useParams();
  const tableRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        setLoading(true);
        let url = `/study/${id}/exports?page=${page + 1}&size=${pageSize}`;

        const res: AxiosResponse = await http.get(url);
        const data = res.data?.data?.data;

        const newData = data?.map((row: any) => ({
          id: row?.id,
          type: row?.event ? eventType[row?.event] : "-",
          fileType: row?.exportType.toUpperCase() || "-",
          fileName: row?.fileName || "-",
          fileSize: row?.fileSize
            ? row?.fileSizeUnit
              ? `${row.fileSize} ${row.fileSizeUnit}`
              : `${row.fileSize}`
            : "-",
          requestedOn: row?.createdAt
            ? DateTime.fromISO(row?.createdAt).toFormat("MM/dd/yyyy HH:mm")
            : "-",
          status: row?.status ? formatEventName(row?.status) : "-",
          downloadable: DateTime.fromISO(row?.expiresOn) > DateTime.now(),
          expiresOn: row?.expiresOn
            ? DateTime.fromISO(row?.expiresOn).toFormat("MM/dd/yyyy HH:mm")
            : "-",
        }));
        setExportData(newData || []);
        setExportDataCount(
          res.data?.data?.meta?.paginationOptions?.totalItems || 0
        );
        setLoading(false);
      } catch (err) {
        setLoading(false);
        errorToastMessage(err as Error);
      }
    };
    fetchDetails();
  }, [id, page, toggle]);

  useEffect(() => {
    const container = tableRef.current;

    const updateShadowClasses = () => {
      if (!container) return;

      const { scrollLeft, scrollWidth, clientWidth } = container;

      if (scrollLeft + clientWidth < scrollWidth - 1) {
        if (!container.classList.contains("shadow-right")) {
          container.classList.add("shadow-right");
        }
      } else if (container.classList.contains("shadow-right")) {
        container.classList.remove("shadow-right");
      }
    };

    if (container) {
      updateShadowClasses();

      container.addEventListener("scrollend", updateShadowClasses);
      window.addEventListener("resize", updateShadowClasses);
    }

    return () => {
      if (container) {
        container.removeEventListener("scrollend", updateShadowClasses);
      }
      window.removeEventListener("resize", updateShadowClasses);
    };
  }, []);

  const handleChangePage = (_1: any, newPage: number) => {
    setPage(newPage);
  };

  const handleDownload = async (exportId: string) => {
    try {
      setDownloadingRowId(exportId);
      let res = await http.post(`/study/${id}/exports/${exportId}/download`);
      const downloadLink = res?.data?.data?.downloadLink;
      const link = document.createElement("a");
      link.setAttribute("href", downloadLink);
      link.setAttribute("download", "export.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toastMessage("success", res.data.message);
      setDownloadingRowId("");
    } catch (err) {
      setDownloadingRowId("");
      errorToastMessage(err as Error);
    }
  };

  const refreshPage = () => {
    setToggle((prev) => !prev);
  };

  return (
    <>
      <StyledHeader
        sx={{
          borderBottom: "1px solid #E5E7EB",
        }}
      >
        <Box sx={{ ...HeaderRightContent, gap: 1 }}>
          <Typography variant="h2" color="text.primary">
            Export
          </Typography>
          <IconButton onClick={refreshPage}>
            <RefreshOutlined />
          </IconButton>
        </Box>
      </StyledHeader>
      <Box sx={{ ...dataTablePageShell("calc(100% - 72px)"), p: 2.5 }}>
        <Box
          sx={{
            ...dataTableListShell,
            p: 2,
            backgroundColor: "#fff",
            borderRadius: "10px",
          }}
        >
          <DataTableLayout
            tableRef={tableRef}
            footer={
              <DataTableFooter
                filteredCount={exportDataCount}
                page={page}
                pageSize={pageSize}
                onPageChange={handleChangePage}
                loading={loading}
              />
            }
          >
            <Table
              sx={TableBorderRadiusTopLeftRight}
              stickyHeader
              className="scrollable-table"
            >
              <TableHead>
                <TableRow>
                  <StyledTableCell sx={{ minWidth: 130 }}>Type</StyledTableCell>
                  <StyledTableCell sx={{ minWidth: 100 }}>
                    File Type
                  </StyledTableCell>
                  <StyledTableCell sx={{ minWidth: 200 }}>
                    File Name
                  </StyledTableCell>
                  <StyledTableCell sx={{ minWidth: 100 }}>
                    File Size
                  </StyledTableCell>
                  <StyledTableCell sx={{ minWidth: 170 }}>
                    Requested On
                  </StyledTableCell>
                  <StyledTableCell>Status</StyledTableCell>
                  <StyledTableCell sx={{ minWidth: 170 }}>
                    Expires On
                  </StyledTableCell>
                  <StyledTableCell
                    sx={{ ...StickyStyle, right: 0 }}
                    className="fixed-col right"
                  >
                    Download
                  </StyledTableCell>
                </TableRow>
              </TableHead>
              {!loading && exportData?.length > 0 && (
                <TableBody>
                  {exportData?.map((row: any) => (
                    <TableRow key={row?.id}>
                      <StyledTableCell>{row?.type}</StyledTableCell>
                      <StyledTableCell>{row?.fileType}</StyledTableCell>
                      <StyledTableCell>{row?.fileName}</StyledTableCell>
                      <StyledTableCell>{row?.fileSize}</StyledTableCell>
                      <StyledTableCell>{row?.requestedOn}</StyledTableCell>
                      <StyledTableCell>
                        <CompletedStatusTag status={row?.status} />
                      </StyledTableCell>
                      <StyledTableCell>{row?.expiresOn}</StyledTableCell>
                      <StyledTableCell
                        sx={{
                          color: "gray",
                          ...StickyStyle,
                          right: 0,
                          minWidth: "110px",
                        }}
                        className="fixed-col right"
                      >
                        {downloadingRowId === row?.id ? (
                          <CircularProgress size={18} />
                        ) : row.downloadable ? (
                          <IconButton
                            onClick={() => handleDownload(row?.id)}
                            disabled={downloadingRowId !== ""}
                          >
                            <CloudDownloadIcon />
                          </IconButton>
                        ) : row?.status === "In Progress" ? (
                          <Typography>In Progress</Typography>
                        ) : row?.status === "Failed" ? (
                          <Typography>Failed</Typography>
                        ) : (
                          <Typography>Expired</Typography>
                        )}
                      </StyledTableCell>
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
            {loading && (
              <Box sx={{ width: "100%" }}>
                <LinearProgress />
              </Box>
            )}
            {!loading && exportData.length === 0 && (
              <NoDataContainer>
                <Typography variant="body1" color="gray">
                  No Data
                </Typography>
              </NoDataContainer>
            )}
          </DataTableLayout>
        </Box>
      </Box>
    </>
  );
};

export default Export;
